import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, Printer, QrCode, Loader2 } from 'lucide-react';
import QRCode from 'qrcode';

/**
 * BusinessQRModal — Iter 197
 *
 * Generates an offline-printable QR code for a business listing. Reuses the
 * already-installed `qrcode` library (no new dependency). All rendering is
 * client-side: there is NO backend endpoint. The QR encodes the public URL
 * (or the claim URL for unclaimed listings).
 *
 * Two variants surfaced via the `claim` prop:
 *   - claim=false  → "Find us on Find-2-Day"  / "Scan to view our profile, offers and events"
 *   - claim=true   → "Claim this listing"      / "Scan to claim your free Find-2-Day profile"
 *                    (admins only, for unclaimed listings)
 *
 * Actions:
 *   - Download PNG : downloads the raw QR code (no branding) so the owner
 *                    can drop it into their own marketing artwork.
 *   - Print        : opens a print-ready branded poster in a new window
 *                    (logo, business name, slogan, QR, footer). Auto-runs
 *                    `window.print()` on load.
 */
const BRAND_PURPLE = '#7c3aed';

const BusinessQRModal = ({ open, onClose, businessId, businessName, businessLogo, businessCity, claim = false }) => {
  const { t } = useTranslation();
  const [dataUrl, setDataUrl] = useState('');
  const [generating, setGenerating] = useState(true);

  // The QR resolves to the public business page. For an unclaimed listing the
  // BusinessDetail page already shows a prominent "Claim this Business" CTA,
  // so the same URL serves both variants. Origin from window.location works
  // on both Preview and Production with zero env coupling.
  const url = businessId
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/business/${businessId}`
    : '';

  // Generate the QR every time the modal opens for a different business.
  // We render to a data-URI (toDataURL) and bind it to an <img>; this avoids
  // the ref-vs-render race that left the preview canvas blank when the ref
  // was still null at the moment the canvas-paint call would have fired.
  useEffect(() => {
    if (!open || !url) return;
    let cancelled = false;
    const opts = {
      errorCorrectionLevel: 'M', // resilient enough to survive logo/brand additions
      margin: 2,
      width: 512,
      color: { dark: '#111827', light: '#ffffff' },
    };
    QRCode.toDataURL(url, opts).then((d) => {
      if (cancelled) return;
      setDataUrl(d);
      setGenerating(false);
    }).catch(() => {
      if (!cancelled) setGenerating(false);
    });
    return () => { cancelled = true; };
  }, [open, url]);

  if (!open) return null;

  const safeName = businessName || 'find-2-day-business';
  const fileSlug = safeName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'business';
  const fileName = `${fileSlug}-qr${claim ? '-claim' : ''}.png`;

  const handleDownload = () => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Render a self-contained printable poster in a popup. We inline ALL styles
  // (no external CSS) so the print window works regardless of where it's
  // opened from. The print call fires AFTER the QR image's `load` event so
  // the rendered output never goes to paper without the QR painted.
  const handlePrint = () => {
    if (!dataUrl) return;
    const headerLine = claim
      ? t('qr.claimHeader', 'Claim this listing on Find-2-Day')
      : t('qr.findHeader', 'Find us on Find-2-Day');
    const slogan = claim
      ? t('qr.claimSlogan', 'Scan to claim your free Find-2-Day profile')
      : t('qr.findSlogan', 'Scan to view our profile, offers and events');
    const subHeader = businessCity
      ? `<p style="font-size:14px;color:#6b7280;margin:0 0 24px;">${escapeHtml(businessCity)}</p>`
      : '';
    const logoBlock = businessLogo
      ? `<img src="${escapeAttr(businessLogo)}" alt="" style="max-height:96px;max-width:240px;object-fit:contain;margin:0 auto 16px;display:block;" crossorigin="anonymous" onerror="this.style.display='none'" />`
      : `<div style="height:8px"></div>`;
    const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(safeName)} — ${escapeHtml(headerLine)}</title>
  <style>
    @page { size: A6 portrait; margin: 0; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body { margin: 0; padding: 0; background: #ffffff; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #111827; }
    .sheet {
      width: 105mm; min-height: 148mm; margin: 0 auto; padding: 14mm 10mm; text-align: center;
      display: flex; flex-direction: column; justify-content: space-between; align-items: center;
    }
    .brand { font-size: 12px; letter-spacing: 0.18em; text-transform: uppercase; color: ${BRAND_PURPLE}; font-weight: 700; margin: 0 0 8px; }
    h1 { font-size: 22px; line-height: 1.2; margin: 8px 0 4px; }
    .header-line { font-size: 14px; color: #4b5563; margin: 12px 0 0; font-weight: 500; }
    .qr-wrap { display: flex; align-items: center; justify-content: center; padding: 12px; border: 2px solid #e5e7eb; border-radius: 12px; background: white; margin: 16px 0; }
    .qr-wrap img { width: 220px; height: 220px; display: block; }
    .slogan { font-size: 13px; color: #374151; margin-top: 14px; line-height: 1.45; font-weight: 500; }
    .footer { font-size: 10px; color: #9ca3af; margin-top: 16px; }
    .footer strong { color: ${BRAND_PURPLE}; }
    .actions { margin-top: 16px; }
    .actions button { padding: 8px 14px; border: 1px solid ${BRAND_PURPLE}; background: ${BRAND_PURPLE}; color: #fff; border-radius: 6px; font-size: 13px; cursor: pointer; }
    @media print { .actions { display: none !important; } }
  </style>
</head>
<body>
  <div class="sheet">
    <div>
      <p class="brand">${claim ? 'Find-2-Day · Free Listing' : 'Find-2-Day · Local Directory'}</p>
      ${logoBlock}
      <h1>${escapeHtml(safeName)}</h1>
      ${subHeader}
      <p class="header-line">${escapeHtml(headerLine)}</p>
    </div>
    <div class="qr-wrap"><img id="qr-img" src="${dataUrl}" alt="QR code" /></div>
    <div>
      <p class="slogan">${escapeHtml(slogan)}</p>
      <p class="footer">Powered by <strong>Find-2-Day</strong> · find-2-day.com</p>
      <div class="actions">
        <button type="button" onclick="window.print()">Print</button>
      </div>
    </div>
  </div>
  <script>
    (function () {
      var img = document.getElementById('qr-img');
      function fire() { try { window.focus(); window.print(); } catch (e) {} }
      if (img && img.complete && img.naturalWidth > 0) {
        setTimeout(fire, 250);
      } else if (img) {
        img.addEventListener('load', function () { setTimeout(fire, 250); });
        img.addEventListener('error', function () { /* leave the manual Print button visible */ });
      }
    })();
  <\/script>
</body>
</html>`;
    const win = window.open('', '_blank', 'noopener,noreferrer,width=560,height=820');
    if (!win) {
      alert(t('qr.popupBlocked', 'Please allow pop-ups for find-2-day.com to print the QR poster.'));
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid="business-qr-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <QrCode className="h-5 w-5 text-white" />
            <h2 className="text-white font-semibold text-base">
              {claim ? t('qr.titleClaim', 'Claim listing QR code') : t('qr.title', 'Your QR code')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-2 -mr-1"
            data-testid="qr-close-btn"
            aria-label={t('close', 'Close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <div className="flex items-center justify-center bg-gray-50 rounded-xl p-4 border border-gray-200">
            {generating || !dataUrl ? (
              <div className="h-60 w-60 flex items-center justify-center text-gray-400">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <img
                src={dataUrl}
                alt="QR code preview"
                className="h-60 w-60 rounded"
                data-testid="qr-image"
              />
            )}
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('qr.linkLabel', 'Direct business URL')}</p>
            <p className="mt-1 text-sm text-purple-700 font-mono truncate" title={url} data-testid="qr-url">{url}</p>
          </div>

          <p className="text-xs text-gray-500 leading-relaxed">
            {claim
              ? t('qr.helpClaim', "Print and hand to the business owner — scanning it lands them on the listing where they can tap Claim this Business.")
              : t('qr.help', "Stick this on your menu, shop window, receipts, business cards or event posters so customers can scan straight to your Find-2-Day page.")}
          </p>

          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleDownload}
              disabled={!dataUrl}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50"
              data-testid="qr-download-btn"
            >
              <Download className="h-4 w-4" />
              {t('qr.download', 'Download PNG')}
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={!dataUrl}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg text-sm font-semibold hover:bg-purple-50 transition-colors disabled:opacity-50"
              data-testid="qr-print-btn"
            >
              <Printer className="h-4 w-4" />
              {t('qr.print', 'Print branded poster')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Tiny HTML/attribute escape utilities for the print template. We don't pull
// in a library — these handle the four entities the inputs can realistically
// produce (business name, city, logo URL).
function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function escapeAttr(s) { return escapeHtml(s); }

export default BusinessQRModal;
